import React, { useState, useRef, useEffect } from 'react';
import { Link, useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Home.css';
import useImageLazyLoad from '../hooks/useImageLazyLoad'; // Import the lazy loading hook
// Removed unused imports - LazyProductCard and ProductCard are not currently used
import FeaturedProductCard from '../components/FeaturedProductCard';
import BlogCard from '../components/BlogCard';
import SkeletonScreen from '../components/SkeletonScreen';
import API from '../api';
import { safeEncodeURIComponent, generateProductUrl } from '../utils/urlEncoding';
import { useIsClient } from '../utils/hydrationSafe';
import { trackEvent } from '../utils/analytics'; // Import consent-aware analytics tracking
import { useAnalytics } from '../hooks/useAnalytics'; // Import cookieless analytics hook
// Import enhanced link generators for clean URLs
import { generateCategoryUrl } from '../utils/enhancedLinkGenerators';
import { getFeaturedBlogPosts } from '../data/blogPosts';

// Import Hero images - optimized versions
import heroCanadaWebpOptimized from '../assets/Hero_canada_temp_1200_q40.webp'; // Optimized 57KB WebP
import heroCanadaAvif from '../assets/Hero_canada.avif'; // AVIF format

// Import mobile hero images - optimized versions
import heroCanadaMobilePng from '../assets/mobile/Hero_canada_mobile.png'; 
import heroCanadaMobileWebpOptimized from '../assets/mobile/Hero_canada_mobile_optimized_fixed.webp'; // Fixed mobile WebP
import heroCanadaMobileAvif from '../assets/mobile/Hero_canada_mobile.avif';

// Function to preload critical featured product images (simplified for better performance)
const preloadFeaturedImages = (products, count = 4) => {
  if (!products || products.length === 0) return;
  
  // Only preload the first 2 images to avoid performance issues
  products.slice(0, Math.min(2, count)).forEach((product, index) => {
    if (product.imageUrl || product.imagePath) {
      const img = new Image();
      img.fetchPriority = 'high';
      img.loading = 'eager';
      img.src = product.imageUrl || product.imagePath;
    }
  });
};

function Home() {
  // Get country state and handlers from Layout context
  const {
    countryCode,
    selectedCountries,
    countries
  } = useOutletContext();

  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if we're on the root path (for different SEO handling)
  const isRootPath = location.pathname === '/';

  // Immediate fallback products for instant display (using skeleton placeholders)
  const immediateFallbackProducts = [
    {
      _id: 'skeleton-1',
      name: 'Loading...',
      shortDescription: 'Featured product loading...',
      imageUrl: null, // No image URL - will show skeleton
      price: null, // No price - will show skeleton
      isSkeletonPlaceholder: true
    },
    {
      _id: 'skeleton-2',
      name: 'Loading...',
      shortDescription: 'Featured product loading...',
      imageUrl: null,
      price: null,
      isSkeletonPlaceholder: true
    },
    {
      _id: 'skeleton-3',
      name: 'Loading...',
      shortDescription: 'Featured product loading...',
      imageUrl: null,
      price: null,
      isSkeletonPlaceholder: true
    },
    {
      _id: 'skeleton-4',
      name: 'Loading...',
      shortDescription: 'Featured product loading...',
      imageUrl: null,
      price: null,
      isSkeletonPlaceholder: true
    }
  ];

  // Local state for Home page UI
  const [heroSearchQuery, setHeroSearchQuery] = useState('');
  const [featuredProducts, setFeaturedProducts] = useState(immediateFallbackProducts); // Start with immediate fallback
  const [contentLoaded, setContentLoaded] = useState(false);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState('');
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  // Keyboard navigation for category filter buttons
  useEffect(() => {
    const handleKeyDown = (e) => {
      const pills = document.querySelectorAll('[role="tab"]');
      if (pills.length === 0) return;
      
      const currentIndex = Array.from(pills).findIndex(pill => pill === document.activeElement);
      
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % pills.length;
        pills[nextIndex].focus();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + pills.length) % pills.length;
        pills[prevIndex].focus();
      }
    };

    // Add event listener when category buttons are present
    const pills = document.querySelectorAll('[role="tab"]');
    if (pills.length > 0) {
      pills.forEach(pill => pill.addEventListener('keydown', handleKeyDown));
    }

    return () => {
      const pills = document.querySelectorAll('[role="tab"]');
      pills.forEach(pill => pill.removeEventListener('keydown', handleKeyDown));
    };
  }, [contentLoaded]);

  // Refs for dropdowns in Home page
  const categoryRefs = useRef([]);

  // Get hero image source based on country code
  const getHeroImageSrc = () => {
    // Import directly here to ensure it's properly loaded
    return require('../assets/Hero_canada.png');
  };

  // Use the lazy loading hook for hero image
  // Set priority to true for hero images since they are above the fold
  useImageLazyLoad(
    getHeroImageSrc(),
    null,  // No placeholder needed as we'll handle loading state differently
    true   // Priority true for hero image as it's above the fold
  );

  const isClient = useIsClient();

  // Ensure consistent initial render for SSR hydration
  // Always render both skeleton and content, but with visibility controlled by CSS
  useEffect(() => {
    // Only run this effect on the client
    if (!isClient) return;
    
    // Wait for hydration to complete before showing content
    const timer = setTimeout(() => {
      setContentLoaded(true);
      // Note: Removed scroll position restoration to allow proper scroll-to-top behavior
      // when navigating between pages (especially with back button)
    }, 800);
    
    return () => clearTimeout(timer);
  }, [isClient]);

  // Mobile detection effect
  useEffect(() => {
    const detectMobile = () => {
      const width = window.innerWidth;
      const userAgent = navigator.userAgent;
      
      // Enhanced mobile/tablet detection logic
      // Priority 1: Check for known mobile/tablet user agents first
      const isMobileDevice = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isTablet = /iPad|Android(?=.*\bMobile\b)|Android(?=.*\bTablet\b)|PlayBook|Silk/i.test(userAgent);
      
      // Priority 2: Specific iPad detection (including iPad Pro/Air that may report as desktop)
      const isIPad = /iPad/i.test(userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints && navigator.maxTouchPoints > 1);
      
      // Priority 3: Touch-capable Android tablets with larger screens
      const isAndroidTablet = /Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
      
      // Priority 4: Screen width fallback for unknown devices
      const isMobileBySize = width < 768;
      
      // Priority 5: Tablet-sized screens (768px to 1024px) should be treated as mobile for button purposes
      const isTabletSize = width >= 768 && width <= 1024;
      
      // Combine all detection methods - prioritize device type over screen size
      const isMobile = isMobileDevice || isTablet || isIPad || isAndroidTablet || isMobileBySize || isTabletSize;
      
      setIsMobileDevice(isMobile);
    };

    // Only run on client side
    if (typeof window !== 'undefined') {
      // Initial detection
      detectMobile();
      
      // Add resize listener
      window.addEventListener('resize', detectMobile);
      
      return () => {
        window.removeEventListener('resize', detectMobile);
      };
    }
  }, []);

  // Fetch featured product IDs and then details (background loading)
  useEffect(() => {
    let isMounted = true;
    async function fetchFeaturedProducts() {
      // Don't show loading spinner since we start with fallback products
      try {
        // 1. Fetch featured product IDs with consistent API path
        let productIds = [];
        try {
          // Use the correct API path with '/api/' prefix
          const idsRes = await API.get('/api/products/featured-ids');
          productIds = idsRes.data.productIds || [];
        } catch (idErr) {
          console.error('Failed to fetch featured product IDs:', idErr);
          // Don't throw - just use fallback products
          return;
        }
        
        if (!productIds.length) {
          console.warn('No featured product IDs returned, using fallback');
          return;
        }
        
        // 2. Fetch product details using _id param (array)
        const detailsRes = await API.get('/api/products', { params: { _id: productIds } });
        let products = Array.isArray(detailsRes.data) ? detailsRes.data : (detailsRes.data.products || []);
        // 3. Order products as per productIds
        products = productIds.map(id => products.find(p => p._id === id)).filter(Boolean);
        
        if (isMounted && products.length > 0) {
          setFeaturedProducts(products);
          // Preload all 4 featured product images for instant loading
          preloadFeaturedImages(products, 4);
        }
      } catch (err) {
        console.error('Error fetching featured products:', err);
        // Keep using current fallback products - don't change anything
      }
    }
    fetchFeaturedProducts();
    return () => { isMounted = false; };
  }, []); // Remove manualFeaturedProducts dependency to avoid loops

  // Preload images immediately for fallback products (disabled for skeletons)
  useEffect(() => {
    // No preloading needed for skeleton placeholders
  }, []);

  // Preload images for the first 4 featured products when content loads (only real products)
  useEffect(() => {
    if (contentLoaded && featuredProducts.length > 0 && !featuredProducts[0]?.isSkeletonPlaceholder) {
      // Only preload when we have real products, not skeletons
      preloadFeaturedImages(featuredProducts, 2); // Reduced to 2 for better performance
    }
  }, [contentLoaded, featuredProducts]);

  // Determine which products to display - always have products to show
  const displayProducts = featuredProducts || immediateFallbackProducts;
  const hasProducts = displayProducts && displayProducts.length > 0;

  // Effect for category fade-in (keep as before)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.2 }
    );

    const currentCategoryRefs = categoryRefs.current;
    currentCategoryRefs.forEach(ref => ref && observer.observe(ref));
    return () => {
      currentCategoryRefs.forEach(ref => ref && observer.unobserve(ref));
    };
  }, []); // categoryRefs.current is stable, so an empty dependency array is acceptable.

  // Effect to prioritize critical resources on mobile
  useEffect(() => {
    // Only run on client side after content is loaded
    if (!isClient || !contentLoaded) return;
    
    // Use a small delay to ensure hydration is complete
    const prioritizeImages = setTimeout(() => {
      // Only run on mobile devices
      if (typeof window !== 'undefined' && 
          (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth < 768)) {
        
        // Immediately prioritize hero image
        const heroImg = document.querySelector('.hero-flag-img');
        if (heroImg) {
          heroImg.setAttribute('fetchpriority', 'high');
          heroImg.setAttribute('loading', 'eager');
          heroImg.setAttribute('decoding', 'sync');
        }
        
        // Prioritize the first four product cards
        const productImgs = document.querySelectorAll('.product-card img');
        for (let i = 0; i < Math.min(4, productImgs.length); i++) {
          if (productImgs[i]) {
            productImgs[i].setAttribute('fetchpriority', 'high');
            productImgs[i].setAttribute('loading', 'eager');
          }
        }
      }
    }, 100); // Small delay to ensure hydration is complete
    
    return () => clearTimeout(prioritizeImages);
  }, [contentLoaded, isClient]);

  // Search submit handler (keep as before)
  // const handleSearchSubmit = (e) => {
  //   e.preventDefault();
  //   // Always navigate, even if question is empty
  //   navigate(`/country/${countryCode}/search?q=${encodeURIComponent(question.trim())}`);
  //   setQuestion(''); // Optionally clear input
  // };

  // Handler for the new hero search bar
  const handleHeroSearchSubmit = (e) => {
    e.preventDefault();
    if (heroSearchQuery.trim()) {
      const trimmedQuery = heroSearchQuery.trim();
      
      // Track hero search in Google Analytics (with consent check)
      trackEvent('search', {
        event_category: 'Search',
        event_label: `Search Term: ${trimmedQuery}`,
        search_term: trimmedQuery,
        search_source: 'home_hero',
        country_code: countryCode
      });
      
      // Save navigation state
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('fromSearch', 'true');
        sessionStorage.setItem('productReferrer', window.location.pathname + window.location.search);
      }
      
      // Use window.location.href to force a full page refresh and ensure search results refresh
      if (typeof window !== 'undefined') {
        window.location.href = `/country/${countryCode}/search?q=${safeEncodeURIComponent(trimmedQuery)}`;
      }
      setHeroSearchQuery(''); // Optionally clear input
    }
  };

  // Import cookieless analytics hook
  const { trackButtonClick, trackAdInteraction } = useAnalytics();

  // Google Analytics tracking handlers for home page buttons
  const handleShopNowClick = () => {
    // Clear all filter-related sessionStorage to reset filters
    sessionStorage.removeItem('selectedCategory');
    sessionStorage.removeItem('selectedSubCategory');
    sessionStorage.removeItem('selectedSubCategories');
    sessionStorage.removeItem('productListScrollPosition');
    
    // Track Shop All button click (works without cookie consent)
    trackButtonClick('shop_now_hero', {
      event_category: 'Navigation',
      event_label: 'Hero Shop All Button',
      event_action: 'shop_all_click',
      button_location: 'hero_section',
      country_code: countryCode,
      selected_countries: [countryCode, ...selectedCountries].filter(Boolean).join(',')
    });
  };

  // toggle dropdowns
  // const togglePrimaryDropdown = () => setShowPrimaryDropdown(v => !v);
  // const toggleMultiPanel       = () => setShowMultiPanel(v => !v);
  // const toggleRegionPopover     = () => setShowRegionPopover(v => !v);

  // ensure this helper is present
  // const selectPrimary = (code) => {
  //   handlePrimarySelect(code);
  //   setShowPrimaryDropdown(false);
  // };

  // Prepare data for dropdowns
  const primaryCountryItem   = countries.find(c => c.code === countryCode);
  // const availableCountries   = countries.filter(c => c.code !== countryCode);

  // generic keyboard handler for menu navigation
  // const handleMenuKeyDown = (e, listRef, buttonRef, close) => {
  //   const items = Array.from(
  //     listRef.current.querySelectorAll('[role^="menuitem"]')
  //   );
  //   let idx = items.indexOf(document.activeElement);
  //   switch (e.key) {
  //     case 'ArrowDown':
  //       e.preventDefault();
  //       items[(idx + 1) % items.length].focus();
  //       break;
  //     case 'ArrowUp':
  //       e.preventDefault();
  //       items[(idx - 1 + items.length) % items.length].focus();
  //       break;
  //     case 'Enter':
  //       e.preventDefault();
  //       document.activeElement.click();
  //       break;
  //     case 'Escape':
  //       e.preventDefault();
  //       close(false);
  //       buttonRef.current.focus();
  //       break;
  //     default:
  //       break;
  //   }
  // };

  const handleProductClick = (productOrId) => {
    // Handle both product object and product ID for backward compatibility
    const productId = typeof productOrId === 'string' ? productOrId : productOrId._id;
    const productName = typeof productOrId === 'object' ? productOrId.name : null;
    
    // Navigate to product page using the SEO-friendly URL with product name
    const productUrl = generateProductUrl(countryCode, productId, productName);
    navigate(productUrl);
  };

  // New handler for hero category buttons - Updated to use clean URLs
  const handleHeroCategoryClick = (filterType) => {
    // Clear any existing state first
    setActiveCategoryFilter(null);
    
    // Update active filter state
    setActiveCategoryFilter(filterType);
    
    // Track category filter click in Google Analytics (with consent check)
    trackEvent('click', {
      event_category: 'Navigation',
      event_label: `Hero Category Filter: ${filterType}`,
      event_action: 'category_filter_click',
      button_location: 'hero_section',
      filter_type: filterType,
      country_code: countryCode
    });
    
    // Map filter types to actual categories
    const categoryMapping = {
      'Baby & Kids': 'Baby & Kids',
      'Gourmet': 'Grocery & Gourmet Foods', // Gourmet maps to food category
      'Home Decor': 'Home Decor', // Direct mapping to Home Decor category
      'Beauty': 'Beauty', // Use Beauty category directly
      'Books': 'Books & Media', // Books maps to Books & Media category
      'Clothing': 'Clothing',
      'Wellness': 'Health & Wellness',
      'Beverage': 'Beverages' // Beverage maps to Beverages category
    };
    
    const category = categoryMapping[filterType];
    
    if (category) {
      try {
        // Generate clean URL and navigate
        const cleanUrl = generateCategoryUrl(countryCode, category);
        
        // Add fresh parameter to ensure filters are reset
        const urlWithFresh = `${cleanUrl}?fresh=true`;
        
        navigate(urlWithFresh);
        
      } catch (error) {
        console.error('ERROR generating clean URL:', error);
        // Fallback to old query format if clean URL generation fails
        const targetUrl = generateCategoryUrl(countryCode, category) + '?fresh=true';
        navigate(targetUrl);
      }
    } else {
      console.error('No category mapping found for filterType:', filterType);
    }
  };

  return (
    <>
      <Helmet>
        {/* Dynamic SEO based on whether we're on root path or country-specific path */}
        {isRootPath ? (
          <>
            {/* Root path SEO - more generic, international */}
            <title>OriginSelect - Shop Canadian-Made Goods Anywhere | Quality Products Made with Heart & Heritage</title>
            <meta 
              name="description" 
              content="Discover 3,000+ products crafted by Canadian brands. Shop sustainable baby products, artisan goods, and authentic local items curated for discerning customers."
            />
            
            {/* Enhanced Open Graph tags for root path */}
            <meta property="og:title" content="OriginSelect - Shop Canadian-Made Goods Anywhere" />
            <meta property="og:description" content="Discover 3,000+ products crafted by Canadian brands. Shop sustainable, artisan-made goods." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://originselect.com/" />
            <meta property="og:image" content="https://originselect.com/Hero_canada.avif" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content="OriginSelect" />
            
            {/* Enhanced Twitter Card tags for root path */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@OriginSelect" />
            <meta name="twitter:title" content="OriginSelect - Shop Canadian-Made Goods Anywhere" />
            <meta name="twitter:description" content="Discover 3,000+ products crafted by Canadian brands." />
            <meta name="twitter:image" content="https://originselect.com/Hero_canada.avif" />
            <meta name="twitter:image:alt" content="OriginSelect - Shop Canadian-Made Goods Anywhere" />
            
            {/* Canonical URL for root path */}
            <link rel="canonical" href="https://originselect.com/" />
            
            {/* Hreflang pairs for root path */}
            <link rel="alternate" hreflang="en-ca" href="https://originselect.com/country/canada/home/" />
            <link rel="alternate" hreflang="x-default" href="https://originselect.com/" />
          </>
        ) : (
          <>
            {/* Country-specific path SEO */}
            <title>{`OriginSelect - ${countryCode === 'canada' ? 'Canada' : 'Canada'} | Quality Products Made with Heart & Heritage`}</title>
            <meta 
              name="description" 
              content={`Discover quality products made with heart and heritage from ${countryCode === 'canada' ? 'Canada' : 'Canada'}. Shop sustainable baby products, artisan goods, and authentic local items curated for discerning customers.`}
            />
            
            {/* Enhanced Open Graph tags for country-specific */}
            <meta property="og:title" content={`OriginSelect - Quality Products from ${countryCode === 'canada' ? 'Canada' : 'Canada'}`} />
            <meta property="og:description" content={`Discover quality products made with heart and heritage from ${countryCode === 'canada' ? 'Canada' : 'Canada'}. Shop sustainable, artisan-made goods.`} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`https://originselect.com/country/${countryCode}/home/`} />
            <meta property="og:image" content="https://originselect.com/Hero_canada.avif" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content="OriginSelect" />
            
            {/* Enhanced Twitter Card tags for country-specific */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@OriginSelect" />
            <meta name="twitter:title" content={`OriginSelect - Quality Products from ${countryCode === 'canada' ? 'Canada' : countryCode === 'usa' ? 'USA' : countryCode.toUpperCase()}`} />
            <meta name="twitter:description" content={`Discover quality products made with heart and heritage from ${countryCode === 'canada' ? 'Canada' : countryCode === 'usa' ? 'the USA' : countryCode}.`} />
            <meta name="twitter:image" content="https://originselect.com/Hero_canada.avif" />
            <meta name="twitter:image:alt" content={`OriginSelect - Quality Products from ${countryCode === 'canada' ? 'Canada' : countryCode === 'usa' ? 'USA' : countryCode.toUpperCase()}`} />
            
            {/* Canonical URL for country-specific path */}
            <link rel="canonical" href={`https://originselect.com/country/${countryCode}/home/`} />
            
            {/* Hreflang pairs for country-specific paths */}
            {countryCode === 'canada' && (
              <>
                <link rel="alternate" hreflang="en-ca" href="https://originselect.com/country/canada/home/" />
                <link rel="alternate" hreflang="x-default" href="https://originselect.com/" />
              </>
            )}
          </>
        )}
        
        {/* Common SEO tags for all paths */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Preconnect to external domains for faster resource loading */}
        <link rel="preconnect" href="https://api.iconify.design" />
        <link rel="preconnect" href="https://apps.apple.com" />
        <link rel="preconnect" href="https://chrome.google.com" />
        
        {/* Preload critical hero image */}
        <link 
          rel="preload" 
          as="image" 
          href={heroCanadaWebpOptimized} 
          fetchpriority="high"
          type="image/webp"
        />
        
        {/* JSON-LD Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "OriginSelect",
            "url": "https://originselect.com",
            "logo": "https://originselect.com/static/logo.png",
            "description": "OriginSelect connects you with authentic, handcrafted products from local artisans. Discover unique, high-quality goods made with heart and heritage.",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "CA"
            },
            "sameAs": [
              "https://www.pinterest.com/originselect_canada/",
              "https://www.instagram.com/OriginSelect/",
              "https://www.youtube.com/@OriginSelect"
            ]
          })}
        </script>
      </Helmet>

      {/* Always render skeleton to ensure consistent server/client rendering */}
      <div style={{ 
        opacity: contentLoaded ? 0 : 1, 
        visibility: contentLoaded ? 'hidden' : 'visible',
        position: contentLoaded ? 'absolute' : 'relative',
        transition: 'opacity 0.3s ease'
      }}>
        <SkeletonScreen 
          type="home" 
          count={4} 
          isVisible={true} 
        />
      </div>

      {/* Always render main content but control visibility with CSS */}
      {/* This ensures identical structure on server and client */}
      <div 
        className="home-page" 
        style={{
          opacity: contentLoaded ? 1 : 0, 
          visibility: contentLoaded ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease'
        }}
      >
        {/* ——— HERO SECTION ——— */}
        <section className="hero-section">
          {/* Hero container with fixed dimensions to prevent layout shift */}
          <div className={`hero-image-container ${heroImageLoaded ? 'loaded' : ''}`}>
            {/* Picture element with mobile-specific optimized images */}
            <picture className="hero-flag-bg-picture">
              {/* Mobile-specific AVIF image - best quality, smallest size */}
              <source 
                media="(max-width: 767px)" 
                srcSet={heroCanadaMobileAvif} 
                type="image/avif" 
                fetchpriority="high"
              />
              {/* Mobile-specific WebP optimized - fixed aspect ratio */}
              <source 
                media="(max-width: 767px)" 
                srcSet={heroCanadaMobileWebpOptimized} 
                type="image/webp" 
                fetchpriority="high"
              />
              {/* Mobile-specific PNG fallback */}
              <source 
                media="(max-width: 767px)" 
                srcSet={heroCanadaMobilePng} 
                type="image/png" 
              />
              {/* Desktop AVIF - best quality, smallest size */}
              <source srcSet={heroCanadaAvif} type="image/avif" fetchpriority="high" />
              {/* Desktop optimized WebP - ~186KB with lazy loading */}
              <source srcSet={heroCanadaWebpOptimized} type="image/webp" fetchpriority="high" />
              {/* Desktop PNG - final fallback for older browsers */}
              <img 
                src={heroCanadaWebpOptimized}
                alt={`${primaryCountryItem ? primaryCountryItem.label : 'Country'} scenic background showcasing local landscape and culture`}
                className="hero-flag-img"
                loading="eager" 
                fetchpriority="high"
                decoding="sync"
                width="1920" 
                height="1080"
                onLoad={() => setHeroImageLoaded(true)}
                onError={() => setHeroImageLoaded(true)}
              />
            </picture>
          </div>
          <div className="hero-content">
            <h1>Handpicked Canadian Goods</h1>
            <p>From local workshops to your doorstep</p>

            {/* New Hero Search Bar */}
            <form onSubmit={handleHeroSearchSubmit} className="hero-search-form">
              <input
                type="text"
                className="hero-search-input"
                placeholder="Search products..."
                value={heroSearchQuery}
                onChange={(e) => setHeroSearchQuery(e.target.value)}
                aria-label="Search products in hero section"
              />
              <button type="submit" className="hero-search-button" aria-label="Submit search">
                <svg className="search-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </button>
            </form>

            {/* Category Filter Buttons */}
            <div className="hero-category-filters" role="tablist" aria-label="Product category filters">
              <button 
                id="category-filter-baby-kids"
                role="tab"
                aria-selected={activeCategoryFilter === 'Baby & Kids'}
                aria-controls="category-content-baby-kids"
                tabIndex={activeCategoryFilter === 'Baby & Kids' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Baby & Kids' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Baby & Kids')}
              >
                Baby & Kids
              </button>
              <button 
                id="category-filter-gourmet"
                role="tab"
                aria-selected={activeCategoryFilter === 'Gourmet'}
                aria-controls="category-content-gourmet"
                tabIndex={activeCategoryFilter === 'Gourmet' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Gourmet' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Gourmet')}
              >
                Gourmet
              </button>
              <button 
                id="category-filter-home-decor"
                role="tab"
                aria-selected={activeCategoryFilter === 'Home Decor'}
                aria-controls="category-content-home-decor"
                tabIndex={activeCategoryFilter === 'Home Decor' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Home Decor' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Home Decor')}
              >
                Home Decor
              </button>
              <button 
                id="category-filter-beauty"
                role="tab"
                aria-selected={activeCategoryFilter === 'Beauty'}
                aria-controls="category-content-beauty"
                tabIndex={activeCategoryFilter === 'Beauty' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Beauty' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Beauty')}
              >
                Beauty
              </button>
              <button 
                id="category-filter-books"
                role="tab"
                aria-selected={activeCategoryFilter === 'Books'}
                aria-controls="category-content-books"
                tabIndex={activeCategoryFilter === 'Books' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Books' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Books')}
              >
                Books
              </button>
              <button 
                id="category-filter-clothing"
                role="tab"
                aria-selected={activeCategoryFilter === 'Clothing'}
                aria-controls="category-content-clothing"
                tabIndex={activeCategoryFilter === 'Clothing' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Clothing' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Clothing')}
              >
                Clothing
              </button>
              <button 
                id="category-filter-wellness"
                role="tab"
                aria-selected={activeCategoryFilter === 'Wellness'}
                aria-controls="category-content-wellness"
                tabIndex={activeCategoryFilter === 'Wellness' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Wellness' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Wellness')}
              >
                Wellness
              </button>
              <button 
                id="category-filter-beverage"
                role="tab"
                aria-selected={activeCategoryFilter === 'Beverage'}
                aria-controls="category-content-beverage"
                tabIndex={activeCategoryFilter === 'Beverage' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Beverage' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Beverage')}
              >
                Beverage
              </button>
            </div>

            <div className="hero-buttons-container"> {/* Container for buttons */}
              <Link
                to={`/country/${countryCode}/products?reset=true`}
                className="hero-shop-now-btn"
                onClick={handleShopNowClick}
              >
                Shop All
              </Link>
            </div>
          </div>
        </section>

        {/* ——— FEATURED PRODUCTS SECTION ——— */}
        <section className="featured-products-section">
          <h2>Featured Products</h2>
          
          <div className="product-grid">
            {hasProducts ? (
              displayProducts.slice(0, 4).map((product, index) => (
                <FeaturedProductCard
                  key={product._id}
                  product={product}
                  countryCode={countryCode}
                  handleProductClick={handleProductClick}
                  index={index}
                />
              ))
            ) : (
              <div className="no-products-message">
                <p>No featured products available at this time.</p>
              </div>
            )}
          </div>
        </section>

        {/* ——— FEATURED ARTICLES SECTION ——— */}
        <section className="featured-articles-section">
          <div className="section-header">
            <h2>Latest Articles</h2>
            <p>Expert guides and insights for Canadian families</p>
          </div>
          
          <div className="articles-grid">
            {getFeaturedBlogPosts().map((post) => (
              <BlogCard
                key={post.id}
                post={post}
                countryCode={countryCode}
              />
            ))}
          </div>

          <div className="articles-cta">
            <Link
              to={`/country/${countryCode}/blog`}
              className="btn secondary-btn"
            >
              View All Articles
            </Link>
          </div>
        </section>

        <section className="about-section">
          <h2>Our Mission</h2>
          <p>
            Championing products made with heart and heritage.
            OriginSelect connects you with locally sourced excellence.
          </p>
          <Link
            to={`/country/${countryCode}/about`}
            className="btn secondary-btn"
          >
            Learn More
          </Link>
        </section>

        {/* Hidden content areas for screen reader aria-controls references */}
        <div className="sr-only" id="category-content-baby-kids" role="tabpanel" aria-labelledby="category-filter-baby-kids">
          Baby & Kids products section
        </div>
        <div className="sr-only" id="category-content-gourmet" role="tabpanel" aria-labelledby="category-filter-gourmet">
          Gourmet products section
        </div>
        <div className="sr-only" id="category-content-home-decor" role="tabpanel" aria-labelledby="category-filter-home-decor">
          Home decor products section
        </div>
        <div className="sr-only" id="category-content-beauty" role="tabpanel" aria-labelledby="category-filter-beauty">
          Beauty products section
        </div>
        <div className="sr-only" id="category-content-books" role="tabpanel" aria-labelledby="category-filter-books">
          Books and media section
        </div>
        <div className="sr-only" id="category-content-clothing" role="tabpanel" aria-labelledby="category-filter-clothing">
          Clothing products section
        </div>
        <div className="sr-only" id="category-content-wellness" role="tabpanel" aria-labelledby="category-filter-wellness">
          Wellness products section
        </div>
        <div className="sr-only" id="category-content-beverage" role="tabpanel" aria-labelledby="category-filter-beverage">
          Beverage products section
        </div>
      </div>
    </>
  );
}

export default Home;
